/**
 * Derive where to resume a BK design from the server checkpoint meta
 * (`get_bk_design_files_meta`).
 *
 * `stepCompletion` lives only in memory and resets to all-false on reload, so
 * without this the dashboard's "proceed" always lands on the earliest step even
 * when the order already has checkpoints saved. We map the *latest* step that
 * has a server checkpoint back to the step the user should resume on (the step
 * that produced it — so they can review/continue it) plus the matching
 * `bkMainProcessIntent` so the step's checkpoint mesh + tool card hydrate.
 *
 * Now that every step saves its own checkpoint this walks the whole step order
 * instead of probing three named slots; orders saved before per-step slots
 * existed still resolve, because `getStepCheckpointEntry` falls back to the
 * legacy shared slots.
 */
import {
  GLOBAL_STEP_ORDER,
  MAIN_PROCESS_STEPS,
} from '../constants/flowConfig';
import {
  getCheckpointMeshUrl,
  getLastCheckpointStepOnServer,
  getLatestFinalFileEntry,
} from './bkScanSourceFiles';

/** Mark every step strictly before `step` (in global order) as complete. */
function completeBefore(step) {
  const completion = {};
  const idx = GLOBAL_STEP_ORDER.indexOf(step);
  if (idx <= 0) return completion;
  for (let i = 0; i < idx; i++) completion[GLOBAL_STEP_ORDER[i]] = true;
  return completion;
}

/**
 * Which mesh the step's loader should hydrate from. Locking and everything after
 * it read the locking checkpoint; the clinical steps read the rectification-side
 * checkpoints; the scan steps start clean.
 */
function intentForStep(step) {
  const idx = GLOBAL_STEP_ORDER.indexOf(step);
  if (idx < 0) return 'fresh';
  if (idx >= GLOBAL_STEP_ORDER.indexOf(MAIN_PROCESS_STEPS.LOCKING_MECHANISM)) {
    return 'modify_locking';
  }
  if (idx >= GLOBAL_STEP_ORDER.indexOf(MAIN_PROCESS_STEPS.RECTIFICATION)) {
    return 'modify_rectification';
  }
  return 'fresh';
}

/**
 * @param {Record<string, unknown> | null | undefined} meta
 * @returns {{ stepCompletion: Record<string, boolean>, resumeStep: string, intent: 'fresh' | 'modify_rectification' | 'modify_locking' }}
 */
export function deriveResumeFromMeta(meta) {
  const finalEntry = getLatestFinalFileEntry(meta);
  const hasFinal = !!finalEntry && getCheckpointMeshUrl(finalEntry) !== '';

  if (hasFinal) {
    // Everything is done — land on Finalization. Keep the locking intent so the
    // finalization preview can still hydrate the saved locking mesh if needed.
    return {
      stepCompletion: completeBefore(MAIN_PROCESS_STEPS.FINALIZATION),
      resumeStep: MAIN_PROCESS_STEPS.FINALIZATION,
      intent: 'modify_locking',
    };
  }

  const lastStep = getLastCheckpointStepOnServer(meta);
  if (lastStep) {
    return {
      stepCompletion: completeBefore(lastStep),
      resumeStep: lastStep,
      intent: intentForStep(lastStep),
    };
  }

  // No checkpoints yet — start the scan flow from the top.
  return {
    stepCompletion: {},
    resumeStep: GLOBAL_STEP_ORDER[0],
    intent: 'fresh',
  };
}
